import { Suspense, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { LoadingState } from '@/components/index.js';
import { isPermitted, requirementFor, type NavItem } from '@/components/index.js';
import { useAuthOptional } from '@/lib/auth/AuthProvider.js';
import { ErrorBoundary } from './RouteError.js';
import { NoAccessPage } from './NoAccessPage.js';

/**
 * Wraps a lazily-loaded screen.
 *
 * Shows a skeleton while the route's code arrives rather than a blank frame,
 * so a slow connection looks like loading rather than like nothing happening.
 *
 * The boundary sits **inside** the shell, so a page that throws — or a chunk
 * that fails to load after a deploy — leaves the sidebar and top bar in place
 * and the user able to go somewhere else. A router-level `errorElement` would
 * replace the whole window and strand them.
 *
 * It is keyed on the pathname so navigating away clears the error. Without
 * that, one failed screen makes every later screen look broken too.
 *
 * It also refuses a screen the navigation would not have offered. Hiding a
 * link is not a guard: typing the URL rendered the admin page in full, with
 * its buttons live and its forms opening, and only the data request refused.
 * The check reads the same declaration the sidebar filters by, so the menu
 * and the guard cannot come to different conclusions.
 */
export function Screen({ children }: { children: ReactNode }) {
  const location = useLocation();
  const auth = useAuthOptional();

  /**
   * Fail closed, but only where something is actually being asked for.
   *
   * Most destinations state no requirement — Home is in the menu and open to
   * everyone — so "in the navigation" is not the test. Where a permission or
   * a team *is* demanded, no session to read means nothing has been checked,
   * which is a reason to refuse rather than to allow.
   */
  const required = requirementFor(location.pathname);
  const demanded = Boolean(required?.permission ?? required?.requiresTeam);
  const permitted =
    !demanded ||
    (auth !== null &&
      isPermitted(required as NavItem, {
        can: auth.can,
        canAny: auth.canAny,
        managesPeople: auth.session?.managesPeople ?? false,
      }));

  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<LoadingState rows={5} />}>
        {permitted ? children : <NoAccessPage />}
      </Suspense>
    </ErrorBoundary>
  );
}
